import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import type { SaveWizardDTO } from '@libs/dtos/production/production.dto';
import type {
	TelegramWebAppCatalogDTO,
	TelegramWebAppClientDTO,
	TelegramWebAppCupsDTO,
	TelegramWebAppSubmitResultDTO,
} from '@libs/dtos/telegram/telegram-webapp.dto';
import { TelegramWebAppService } from '@services/telegram/telegram-webapp.service';
import { TARIFF_LABELS } from '@shared/constants/domain.const';
import { SharedModule } from '@shared/modules/shared.module';
import { formatDate } from '@shared/utils/format';
import { debounceTime, distinctUntilChanged, Subject, switchMap } from 'rxjs';
import { defaultContractEndDate } from '../../backoffice/production/wizard/production-contract-date.util';
import { recoverTelegramInitData, waitForTelegramInitData } from './telegram-init-data';

/** The part of Telegram's Mini App API this form uses. */
interface TelegramWebApp {
	initData: string;
	ready(): void;
	expand(): void;
	close(): void;
}

declare global {
	interface Window {
		Telegram?: { WebApp: TelegramWebApp };
	}
}

const TELEGRAM_SCRIPT = 'https://telegram.org/js/telegram-web-app.js';

type Mode = 'NEW' | 'RENEWAL';
type Phase = 'loading' | 'outside' | 'unsigned' | 'forbidden' | 'unavailable' | 'form' | 'done';

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));
type Tariff = SaveWizardDTO['contract']['tariff'];

/** Loads Telegram's script once; resolves even if it fails, so the page can say what went wrong. */
function loadTelegramScript(): Promise<void> {
	if (window.Telegram?.WebApp) return Promise.resolve();
	return new Promise((resolve) => {
		const script = document.createElement('script');
		script.src = TELEGRAM_SCRIPT;
		script.onload = () => resolve();
		script.onerror = () => resolve();
		document.head.appendChild(script);
	});
}

/** Spanish keyboards type a comma: "4500,5" is a number too. */
function parseDecimal(value: string | number | null | undefined): number | null {
	if (value === null || value === undefined || value === '') return null;
	const parsed = Number(String(value).replace(/\s/g, '').replace(',', '.'));
	return Number.isFinite(parsed) ? parsed : null;
}

const toIsoDate = (date: Date): string =>
	`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/**
 * Mini App for altas and renewals (docs/BOT_TELEGRAM.md §5.5), opened from the
 * bot with /alta or /renovar. It asks for exactly what the web wizard requires;
 * what is optional can be finished later on the computer, and the production is
 * then marked incomplete. The server validates it as the real save before it
 * reaches the approval tray.
 */
@Component({
	selector: 'app-telegram-alta',
	imports: [SharedModule],
	templateUrl: './telegram-alta.component.html',
	styleUrl: './telegram-alta.component.scss',
})
export class TelegramAltaComponent implements OnInit {
	private readonly api = inject(TelegramWebAppService);
	private readonly route = inject(ActivatedRoute);
	private readonly transloco = inject(TranslocoService);
	private readonly destroyRef = inject(DestroyRef);

	readonly tariffs = Object.entries(TARIFF_LABELS).map(([value, label]) => ({ value: value as Tariff, label }));

	phase: Phase = 'loading';
	mode: Mode = 'NEW';
	userName = '';
	private initData = '';
	/** One per opening: a double tap or a retried approval reuses the same save. */
	private readonly requestId = crypto.randomUUID();

	catalog: TelegramWebAppCatalogDTO = { marketers: [], products: [], collaborators: [] };

	// Client
	clientMode: 'existing' | 'new' = 'existing';
	search = '';
	private readonly search$ = new Subject<string>();
	results: TelegramWebAppClientDTO[] = [];
	searching = false;
	client: TelegramWebAppClientDTO | null = null;
	newClient = { type: 'COMPANY' as 'COMPANY' | 'INDIVIDUAL', name: '', taxId: '', iban: '', phone: '', email: '' };

	// Supply point
	cupsMode: 'existing' | 'new' = 'existing';
	clientCups: TelegramWebAppCupsDTO[] = [];
	cupsId: number | null = null;
	newCups = { code: '', supplyAddress: '' };

	// Contract
	marketerId: number | null = null;
	product = '';
	tariff: Tariff = 'T_20TD';
	startDate = '';
	endDate = '';
	powers: Record<number, string> = { 1: '', 2: '', 3: '', 4: '', 5: '', 6: '' };
	consumption = '';
	collaboratorId: number | null = null;

	sending = false;
	errors: string[] = [];
	result: TelegramWebAppSubmitResultDTO | null = null;

	ngOnInit(): void {
		this.mode = this.route.snapshot.queryParamMap.get('modo') === 'renovar' ? 'RENEWAL' : 'NEW';
		this.search$
			.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				switchMap((term) => {
					this.searching = true;
					return this.api.searchClients(this.initData, term);
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe({
				next: (clients) => {
					this.results = clients;
					this.searching = false;
				},
				error: () => (this.searching = false),
			});
		void this.start();
	}

	private async start(): Promise<void> {
		await loadTelegramScript();
		const webApp = window.Telegram?.WebApp;
		// ready() first: some Desktop/Web builds fill initData only after it.
		webApp?.ready();
		webApp?.expand();

		this.initData = await waitForTelegramInitData(() => webApp?.initData || recoverTelegramInitData(), wait);
		if (!this.initData) {
			// Chrome suelto → outside. Webview de Telegram sin firma → unsigned.
			this.phase = webApp || window.Telegram ? 'unsigned' : 'outside';
			return;
		}

		this.api.session(this.initData).subscribe({
			next: (session) => {
				this.userName = session.name;
				if (!session.canSubmit) {
					this.phase = 'forbidden';
					return;
				}
				this.api.catalog(this.initData).subscribe({
					next: (catalog) => {
						this.catalog = catalog;
						this.phase = 'form';
					},
					error: () => (this.phase = 'unavailable'),
				});
			},
			error: (error: HttpErrorResponse) => {
				this.phase = error.status === 403 ? 'forbidden' : error.status === 401 ? 'unsigned' : 'unavailable';
			},
		});
	}

	t(key: string, params?: Record<string, unknown>): string {
		return this.transloco.translate(key, params);
	}

	// ---------- client ----------

	get canCreateClient(): boolean {
		// A renewal starts from a supply that already has a contract, so its client exists.
		return this.mode === 'NEW';
	}

	onSearch(term: string): void {
		this.search = term;
		if (term.trim().length < 2) {
			this.results = [];
			return;
		}
		this.search$.next(term.trim());
	}

	selectClient(client: TelegramWebAppClientDTO): void {
		this.client = client;
		this.results = [];
		this.search = '';
		this.cupsId = null;
		this.clientCups = [];
		this.cupsMode = 'existing';
		this.api.clientCups(this.initData, client.id).subscribe({
			next: (cups) => {
				this.clientCups = cups;
				if (this.mode === 'NEW' && this.availableCups.length === 0) this.cupsMode = 'new';
			},
			error: () => (this.errors = [this.t('telegramApp.errors.generic')]),
		});
	}

	clearClient(): void {
		this.client = null;
		this.clientCups = [];
		this.cupsId = null;
	}

	setClientMode(mode: 'existing' | 'new'): void {
		this.clientMode = mode;
		this.clearClient();
		// A new client has no supply points yet: its CUPS is new by definition.
		this.cupsMode = mode === 'new' ? 'new' : 'existing';
	}

	// ---------- supply point ----------

	/**
	 * The contract a renewal of this supply starts from: the one in force or, failing
	 * that, the expired one still open to renewal (14/10: allowed with no time margin).
	 */
	renewalOrigin(cups: TelegramWebAppCupsDTO): TelegramWebAppCupsDTO['activeContract'] {
		return cups.activeContract ?? cups.renewableContract;
	}

	/** The origin's end date as the user reads it (dd/mm/yyyy). */
	originEndDate(contract: NonNullable<TelegramWebAppCupsDTO['activeContract']>): string {
		return formatDate(contract.endDate);
	}

	/** The web's rule: an alta goes on a supply without an active contract; a renewal on one with an origin. */
	get availableCups(): TelegramWebAppCupsDTO[] {
		return this.clientCups.filter((cups) =>
			this.mode === 'RENEWAL' ? !!this.renewalOrigin(cups) : !cups.activeContract,
		);
	}

	get selectedCups(): TelegramWebAppCupsDTO | null {
		return this.clientCups.find((cups) => cups.id === this.cupsId) ?? null;
	}

	selectCups(cups: TelegramWebAppCupsDTO): void {
		this.cupsId = cups.id;
		this.tariff = cups.tariff;
		// A renewal may change marketer and product (14/09); start from the origin's ones.
		const origin = this.renewalOrigin(cups);
		if (this.mode === 'RENEWAL' && origin) {
			this.marketerId =
				this.catalog.marketers.find((marketer) => marketer.name === origin.marketerName)?.id ?? null;
			this.product = origin.product ?? '';
		}
	}

	// ---------- contract ----------

	get isGas(): boolean {
		return this.tariff.startsWith('GAS_');
	}

	get powerPeriods(): number[] {
		if (this.isGas) return [];
		return this.tariff === 'T_20TD' ? [1, 2] : [1, 2, 3, 4, 5, 6];
	}

	onStartDate(value: string): void {
		this.startDate = value;
		const start = value ? new Date(`${value}T00:00:00`) : null;
		const end = defaultContractEndDate(start);
		if (end) this.endDate = toIsoDate(end);
	}

	// ---------- what is missing ----------

	/** What the web wizard refuses to save without. */
	private missingRequired(): string[] {
		const missing: string[] = [];
		if (this.clientMode === 'existing' && !this.client) missing.push(this.t('telegramApp.fields.client'));
		if (this.clientMode === 'new') {
			if (!this.newClient.name.trim()) missing.push(this.t('telegramApp.fields.name'));
			if (!this.newClient.taxId.trim()) missing.push(this.t('telegramApp.fields.taxId'));
			if (!this.newClient.iban.trim()) missing.push(this.t('telegramApp.fields.iban'));
		}
		if (this.cupsMode === 'existing' && !this.cupsId) missing.push(this.t('telegramApp.fields.cups'));
		if (this.cupsMode === 'new' && !this.newCups.code.trim()) missing.push(this.t('telegramApp.fields.cupsCode'));
		if (!this.marketerId) missing.push(this.t('telegramApp.fields.marketer'));
		if (!this.startDate) missing.push(this.t('telegramApp.fields.startDate'));
		if (!this.endDate) missing.push(this.t('telegramApp.fields.endDate'));
		// The collaborator's rule is checked against the consumption: the server demands it.
		if (this.collaboratorId && parseDecimal(this.consumption) === null)
			missing.push(this.t('telegramApp.fields.consumption'));
		return missing;
	}

	/** The incomplete marker's rule (§5.5), said before sending. */
	get incompleteParts(): string[] {
		return [
			this.t('telegramApp.parts.documents'),
			...(parseDecimal(this.consumption) === null ? [this.t('telegramApp.parts.consumption')] : []),
			...(this.product ? [] : [this.t('telegramApp.parts.product')]),
		];
	}

	private validate(): string[] {
		const errors: string[] = [];
		const missing = this.missingRequired();
		if (missing.length > 0) errors.push(this.t('telegramApp.missing', { fields: missing.join(', ') }));
		if (this.startDate && this.endDate && this.endDate <= this.startDate)
			errors.push(this.t('telegramApp.errors.endBeforeStart'));
		if (
			this.clientMode === 'new' &&
			this.newClient.email &&
			!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.newClient.email)
		) {
			errors.push(this.t('telegramApp.errors.email'));
		}
		if (this.mode === 'RENEWAL' && this.selectedCups && !this.renewalOrigin(this.selectedCups)) {
			errors.push(this.t('telegramApp.errors.noActiveContract'));
		}
		return errors;
	}

	private buildForm(): SaveWizardDTO {
		const power = (period: number): number | null =>
			this.powerPeriods.includes(period) ? parseDecimal(this.powers[period]) : null;
		return {
			requestId: this.requestId,
			operationIntent: this.mode,
			originContractId:
				this.mode === 'RENEWAL' && this.selectedCups ? this.renewalOrigin(this.selectedCups)?.id : undefined,
			client:
				this.clientMode === 'existing' && this.client
					? { id: this.client.id }
					: {
							type: this.newClient.type,
							name: this.newClient.name.trim(),
							taxId: this.newClient.taxId.trim(),
							iban: this.newClient.iban.trim(),
							phone: this.newClient.phone.trim() || null,
							email: this.newClient.email.trim() || null,
						},
			cups:
				this.cupsMode === 'existing' && this.cupsId
					? { mode: 'EXISTING', id: this.cupsId }
					: {
							mode: 'NEW',
							code: this.newCups.code.trim(),
							supplyAddress: this.newCups.supplyAddress.trim() || null,
						},
			contract: {
				marketerId: this.marketerId as number,
				startDate: this.startDate,
				endDate: this.endDate,
				product: this.product || null,
				tariff: this.tariff,
				powerP1: power(1),
				powerP2: power(2),
				powerP3: power(3),
				powerP4: power(4),
				powerP5: power(5),
				powerP6: power(6),
			},
			consumptionKwh: parseDecimal(this.consumption),
			collaboratorId: this.collaboratorId,
		};
	}

	submit(): void {
		if (this.sending) return;
		this.errors = this.validate();
		if (this.errors.length > 0) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
			return;
		}
		this.sending = true;
		this.api.submit(this.initData, this.buildForm()).subscribe({
			next: (result) => {
				this.sending = false;
				this.result = result;
				this.phase = 'done';
			},
			error: (error: HttpErrorResponse) => {
				this.sending = false;
				if (error.status === 401) {
					this.errors = [this.t('telegramApp.errors.sessionExpired')];
				} else {
					// The server's own words: the same refusal the web would show.
					const details = Array.isArray(error.error?.errors)
						? error.error.errors.filter(
								(message: unknown): message is string => typeof message === 'string',
							)
						: [];
					const message = typeof error.error?.message === 'string' ? error.error.message : null;
					this.errors = details.length > 0 ? details : [message ?? this.t('telegramApp.errors.generic')];
				}
				window.scrollTo({ top: 0, behavior: 'smooth' });
			},
		});
	}

	close(): void {
		window.Telegram?.WebApp.close();
	}
}
